import React, { useEffect, useState } from 'react';
import {
  MessageSquare,
  Users,
  Clock,
  Bot,
  UserCheck,
  CheckCircle,
  Wifi,
  WifiOff,
  RefreshCw,
  Phone,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Activity,
  Zap,
  ArrowRight,
  BarChart3,
  PhoneCall
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { getDashboardMetrics, getConversations, getSystemStatus } from '../lib/api';
import { cn, formatTime, getInitials } from '../lib/utils';
import { toast } from 'sonner';

const WhatsAppIcon = ({ size = 20, className }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';

// ─── Metric Card ───
const MetricCard = ({ title, value, icon: Icon, color, subtitle, trend, trendValue, badge }) => (
  <Card className="bg-white border border-slate-200 hover:shadow-md transition-all duration-300 group">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold tracking-tight text-slate-900">{value}</p>
            {badge && (
              <Badge variant="outline" className={cn("text-[10px] border", badge.className)}>
                {badge.label}
              </Badge>
            )}
          </div>
          {subtitle && (
            <p className="text-xs text-slate-400">{subtitle}</p>
          )}
          {trend && (
            <div className={cn(
              "flex items-center gap-1 text-xs font-medium mt-0.5",
              trend === 'up' ? 'text-emerald-600' : 'text-red-500'
            )}>
              {trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={cn(
          "p-3 rounded-xl transition-transform group-hover:scale-110",
          color === 'primary' && "bg-primary/10",
          color === 'yellow' && "bg-amber-50",
          color === 'green' && "bg-emerald-50",
          color === 'blue' && "bg-blue-50",
          color === 'purple' && "bg-violet-50",
          color === 'orange' && "bg-orange-50",
          color === 'red' && "bg-red-50"
        )}>
          <Icon className={cn(
            "w-6 h-6",
            color === 'primary' && "text-primary",
            color === 'yellow' && "text-amber-500",
            color === 'green' && "text-emerald-500",
            color === 'blue' && "text-blue-500",
            color === 'purple' && "text-violet-500",
            color === 'orange' && "text-orange-500",
            color === 'red' && "text-red-500"
          )} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── Status Indicator ───
const StatusIndicator = ({ status, label }) => (
  <div className="flex items-center gap-2">
    <div className={cn(
      "w-2.5 h-2.5 rounded-full",
      status === 'online' && "bg-emerald-500 animate-pulse",
      status === 'offline' && "bg-red-500",
      status === 'warning' && "bg-amber-500 animate-pulse"
    )} />
    <span className="text-sm text-slate-600 font-medium">{label}</span>
  </div>
);

// ─── Progress Bar ───
const ProgressItem = ({ label, value, total, color, icon: Icon }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className={cn("text-slate-400", color)} />}
          <span className="text-sm text-slate-600">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-800">{value}</span>
          <span className="text-xs text-slate-400">({pct}%)</span>
        </div>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all duration-500", color || "bg-primary")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ─── Chart Tooltip ───
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg">
        <p className="text-sm font-semibold text-slate-800 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-medium">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const Dashboard = ({ onNavigate }) => {
  const [metrics, setMetrics] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    try {
      const response = await getDashboardMetrics();
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const response = await getSystemStatus();
      setSystemStatus(response.data);
    } catch (error) {
      console.error('Error fetching system status:', error);
    }
  };

  const fetchRecentConversations = async () => {
    try {
      const [activeRes, closedRes] = await Promise.all([
        getConversations({ limit: 30 }),
        getConversations({ status: 'closed', limit: 30 })
      ]);

      const allConversations = [...(activeRes.data || []), ...(closedRes.data || [])];
      allConversations.sort((a, b) => {
        const timeA = new Date(a.last_message_time || a.updated_at || 0);
        const timeB = new Date(b.last_message_time || b.updated_at || 0);
        return timeB - timeA;
      });

      setRecentConversations(allConversations.slice(0, 10));
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchMetrics(), fetchRecentConversations(), fetchSystemStatus()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleConversationClick = (conv) => {
    if (onNavigate) {
      onNavigate('conversations', conv.id);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <RefreshCw className="w-7 h-7 animate-spin text-primary" />
          </div>
          <p className="text-slate-500 font-medium">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  const convMetrics = metrics?.conversations || {};
  const attMetrics = metrics?.attendants || {};
  const totalConv = convMetrics.total || 0;

  // Pie data
  const conversationsPieData = [
    { name: 'Bot', value: convMetrics.bot || 0, color: '#94a3b8' },
    { name: 'Aguardando', value: convMetrics.waiting || 0, color: '#f59e0b' },
    { name: 'Ativos', value: convMetrics.active || 0, color: '#10b981' },
    { name: 'Encerrados', value: convMetrics.closed || 0, color: '#6366f1' }
  ].filter(item => item.value > 0);

  // Simulated hourly data
  const hourlyData = [
    { hour: '00h', mensagens: 5, conversas: 2 },
    { hour: '06h', mensagens: 12, conversas: 4 },
    { hour: '08h', mensagens: 45, conversas: 15 },
    { hour: '10h', mensagens: 78, conversas: 25 },
    { hour: '12h', mensagens: 65, conversas: 20 },
    { hour: '14h', mensagens: 89, conversas: 30 },
    { hour: '16h', mensagens: 72, conversas: 22 },
    { hour: '18h', mensagens: 55, conversas: 18 },
    { hour: '20h', mensagens: 35, conversas: 10 },
    { hour: '22h', mensagens: 15, conversas: 5 }
  ];

  const getStatusBadge = (status) => {
    const config = {
      bot: { label: 'Bot', className: 'bg-slate-100 text-slate-600 border-slate-200' },
      waiting: { label: 'Aguardando', className: 'bg-amber-50 text-amber-600 border-amber-200' },
      active: { label: 'Ativo', className: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
      closed: { label: 'Encerrado', className: 'bg-blue-50 text-blue-600 border-blue-200' }
    };
    const { label, className } = config[status] || { label: status, className: '' };
    return <Badge variant="outline" className={cn("text-xs border", className)}>{label}</Badge>;
  };

  const getWebhookIndicator = () => {
    const status = systemStatus?.webhook?.status;
    if (status === 'online') return { status: 'online', label: 'Webhook Online' };
    if (status === 'not_configured') return { status: 'warning', label: 'Webhook não configurado' };
    if (status === 'invalid_url') return { status: 'warning', label: 'Webhook inválido' };
    if (status === 'unknown') return { status: 'warning', label: 'Webhook verificando' };
    if (status === 'offline') return { status: 'offline', label: 'Webhook Offline' };
    return { status: 'warning', label: 'Webhook desconhecido' };
  };

  const webhookIndicator = getWebhookIndicator();

  return (
    <div className="flex-1 overflow-auto bg-slate-50" data-testid="dashboard-page">
      <div className="p-8 space-y-6 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Dashboard
            </h1>
            <p className="text-slate-500 mt-0.5 flex items-center gap-2 text-sm">
              <Activity size={14} className="text-emerald-500" />
              Monitoramento em tempo real
            </p>
          </div>
          <Button variant="outline" onClick={fetchAll} className="gap-2 bg-white">
            <RefreshCw size={16} />
            Atualizar
          </Button>
        </div>

        {/* System Status Bar */}
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-6">
                <StatusIndicator
                  status={webhookIndicator.status}
                  label={webhookIndicator.label}
                />
                <StatusIndicator
                  status={systemStatus?.instances?.connected > 0 ? 'online' : 'offline'}
                  label={`${systemStatus?.instances?.connected || 0}/${systemStatus?.instances?.total || 0} Instâncias`}
                />
                <StatusIndicator
                  status={systemStatus?.chat?.enabled ? 'online' : 'warning'}
                  label={systemStatus?.chat?.enabled ? 'Chat Ativo' : 'Chat Inativo'}
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Zap size={12} className="text-primary" />
                Atualização automática a cada 30s
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total de Conversas"
            value={convMetrics.total || 0}
            icon={MessageSquare}
            color="primary"
            subtitle="Todas as conversas"
          />
          <MetricCard
            title="Aguardando"
            value={convMetrics.waiting || 0}
            icon={Clock}
            color="yellow"
            subtitle="Precisam de atendimento"
            badge={convMetrics.waiting > 0 ? { label: 'Atenção', className: 'bg-amber-50 text-amber-600 border-amber-200' } : undefined}
          />
          <MetricCard
            title="Em Atendimento"
            value={convMetrics.active || 0}
            icon={UserCheck}
            color="green"
            subtitle="Conversas ativas agora"
          />
          <MetricCard
            title="Mensagens Hoje"
            value={metrics?.messages_today || 0}
            icon={BarChart3}
            color="blue"
            subtitle="Total do dia"
          />
        </div>

        {/* Charts + Distribution Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Area Chart - Activity */}
          <Card className="lg:col-span-2 bg-white border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <TrendingUp size={18} className="text-primary" />
                Atividade por Horário
              </CardTitle>
              <CardDescription className="text-slate-400 text-xs">Volume de mensagens e conversas ao longo do dia</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={hourlyData}>
                    <defs>
                      <linearGradient id="colorMensagens" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorConversas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="hour" stroke="#94a3b8" fontSize={12} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      height={36}
                      formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
                    />
                    <Area
                      type="monotone"
                      dataKey="mensagens"
                      name="Mensagens"
                      stroke="#6366f1"
                      fillOpacity={1}
                      fill="url(#colorMensagens)"
                      strokeWidth={2.5}
                    />
                    <Area
                      type="monotone"
                      dataKey="conversas"
                      name="Conversas"
                      stroke="#10b981"
                      fillOpacity={1}
                      fill="url(#colorConversas)"
                      strokeWidth={2.5}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Conversation Distribution with Progress Bars */}
          <Card className="bg-white border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Activity size={18} className="text-primary" />
                Distribuição de Conversas
              </CardTitle>
              <CardDescription className="text-slate-400 text-xs">Status atual de todas as conversas</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-2">
              <ProgressItem label="No Bot" value={convMetrics.bot || 0} total={totalConv} color="bg-slate-400" icon={Bot} />
              <ProgressItem label="Aguardando" value={convMetrics.waiting || 0} total={totalConv} color="bg-amber-500" icon={Clock} />
              <ProgressItem label="Em Atendimento" value={convMetrics.active || 0} total={totalConv} color="bg-emerald-500" icon={UserCheck} />
              <ProgressItem label="Encerradas" value={convMetrics.closed || 0} total={totalConv} color="bg-indigo-500" icon={CheckCircle} />

              {/* Mini pie chart */}
              <div className="pt-2 border-t border-slate-100">
                <div className="h-[130px]">
                  {conversationsPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={conversationsPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={35}
                          outerRadius={55}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {conversationsPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-slate-400">
                      Nenhuma conversa
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row: Equipe + Instâncias + Conversas Recentes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Equipe / Attendants Summary */}
          <Card className="bg-white border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <UserCheck size={18} className="text-primary" />
                Equipe
              </CardTitle>
              <CardDescription className="text-slate-400 text-xs">Status dos atendentes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <UserCheck size={18} className="text-emerald-600" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-emerald-700">Online</span>
                    <p className="text-xs text-emerald-500">Disponível para atendimento</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-emerald-700">{attMetrics.online || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-200/60">
                    <UserCheck size={18} className="text-slate-500" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-slate-700">Total</span>
                    <p className="text-xs text-slate-400">Todos os atendentes</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-slate-700">{attMetrics.total || 0}</span>
              </div>

              {/* Instances status */}
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Instâncias WhatsApp</p>
                {metrics?.instances && metrics.instances.length > 0 ? (
                  <div className="space-y-2">
                    {metrics.instances.map((inst) => (
                      <div key={inst.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="flex items-center gap-2">
                          {inst.status === 'connected' ? (
                            <WhatsAppIcon size={14} className="text-emerald-500" />
                          ) : (
                            <WhatsAppIcon size={14} className="text-slate-400" />
                          )}
                          <span className="text-sm text-slate-600 truncate">{inst.name}</span>
                        </div>
                        <Badge variant="outline" className={cn(
                          "text-[10px] border",
                          inst.status === 'connected'
                            ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                            : "bg-slate-100 text-slate-400 border-slate-200"
                        )}>
                          {inst.status === 'connected' ? 'Online' : 'Offline'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-3">Nenhuma instância</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Conversations */}
          <Card className="lg:col-span-2 bg-white border border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <MessageSquare size={18} className="text-primary" />
                    Conversas Recentes
                  </CardTitle>
                  <CardDescription className="text-slate-400 text-xs">Últimas 10 conversas com atividade</CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="text-primary text-xs gap-1" onClick={() => onNavigate && onNavigate('conversations')}>
                  Ver todas
                  <ArrowRight size={14} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[340px]">
                {recentConversations.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                      <MessageSquare className="w-7 h-7 text-slate-300" />
                    </div>
                    <p className="font-medium">Nenhuma conversa encontrada</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {recentConversations.map((conv) => (
                      <div
                        key={conv.id}
                        className="px-5 py-3.5 hover:bg-slate-50/80 cursor-pointer transition-colors"
                        onClick={() => handleConversationClick(conv)}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <Avatar className="w-10 h-10 shrink-0">
                              {conv.customer_avatar_url && (
                                <AvatarImage
                                  src={conv.customer_avatar_url.startsWith('http')
                                    ? conv.customer_avatar_url
                                    : `${BACKEND_URL}${conv.customer_avatar_url}`}
                                  alt={conv.customer_name || conv.customer_phone}
                                />
                              )}
                              <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                                {getInitials(conv.customer_name || conv.customer_phone)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate text-sm">
                                {conv.customer_name || conv.customer_phone}
                              </p>
                              <p className="text-xs text-slate-400 truncate">
                                {conv.last_message || 'Sem mensagens'}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 shrink-0">
                            {getStatusBadge(conv.status)}
                            <span className="text-[11px] text-slate-400">
                              {formatTime(conv.last_message_time || conv.updated_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
